import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { OutreachPitchKind } from 'fym-shared/src/music24/outreach/dailyPitches';
import { useTRPCAPI } from '../../../../trpc/trpc';
import type { CampaignMessagesPage, PeopleDay, PeopleResult } from '../outreachTypes';
import { useRetry } from './useRetry';

export interface PeopleFilter {
  kind: OutreachPitchKind | null;
  result: PeopleResult | null;
}

/**
 * Joins the pages of the People list: a day can go on over two pages, so the rows of the same day
 * go into one group. A row that a new pitch moved onto the next page shows once.
 */
export function joinPeopleDays(pages: Array<CampaignMessagesPage | null | undefined>): PeopleDay[] {
  const days: PeopleDay[] = [];
  const seen = new Set<string>();
  for (const page of pages) {
    for (const day of page?.days ?? []) {
      const rows = day.rows.filter((row) => !seen.has(row.messageUuid));
      rows.forEach((row) => seen.add(row.messageUuid));
      const last = days[days.length - 1];
      if (last !== undefined && last.day === day.day) {
        last.rows = [...last.rows, ...rows];
      } else {
        days.push({ ...day, rows });
      }
    }
  }
  return days;
}

/**
 * `outreach.campaigns.messages` (design outreach-v2 g1): the people that Plug pitched, by day, one
 * page at a time. The realtime events of the campaign read it again (`useOutreachRealtime`, area
 * `campaigns`).
 */
export function useCampaignPeople(accountId: number, campaignUuid: string, filter: PeopleFilter) {
  const trpcAPI = useTRPCAPI();
  const input = useMemo(
    () => ({ accountId, campaignUuid, kind: filter.kind, result: filter.result }),
    [accountId, campaignUuid, filter.kind, filter.result]
  );
  const query = useInfiniteQuery(
    trpcAPI.outreach.campaigns.messages.infiniteQueryOptions(input, {
      getNextPageParam: (page: CampaignMessagesPage | null) => page?.nextCursor ?? undefined,
      retry: false,
    })
  );
  const pages = query.data?.pages as Array<CampaignMessagesPage | null> | undefined;
  const days = useMemo(() => joinPeopleDays(pages ?? []), [pages]);
  const retry = useRetry(query.refetch);
  return {
    days,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}
